/**
 * PATH: apps/quote/src/pages/import-quote/ImportExistingQuotesPanel.tsx
 * FILE_ID: QUOTE-IMPORT-EXISTING-PANEL-001
 * PURPOSE: Searchable existing-quote picker — tick families, untick revisions.
 * WHY: Structured Quotech quotes copy via duplicate/from-sheet, never AI parse.
 */
import { Copy, Loader2, Search } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { useExistingQuoteImport } from './useExistingQuoteImport'
import type { QuoteFamily } from './existingQuoteTypes'

interface Props {
  jobId?: string | null
  importing: boolean
  onImport: (ids: string[]) => void
}

export function ImportExistingQuotesPanel({ jobId, importing, onImport }: Props) {
  const state = useExistingQuoteImport(jobId)
  const busy = importing || state.importMutation.isPending
  const canImport = !busy && state.selectedMembers.length > 0

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={state.search}
          onChange={(e) => state.setSearch(e.target.value)}
          placeholder="Search existing quotes…"
          className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Tick a quote to bring every revision and variation in that family.
        Untick any revision you do not want. Each selected quote becomes its own builder quote.
        This list is builder quotes only.
      </p>
      <a
        href={`https://crm.quotech.ai/quotes?intent=create-job&search=${encodeURIComponent(state.search)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex text-xs font-medium text-blue-600 hover:underline"
      >
        Search in CRM
      </a>
      <ul className="space-y-2 max-h-[45vh] overflow-auto pr-1">
        {state.listLoading ? (
          <li className="text-sm text-gray-500 px-2 py-6">Loading quotes…</li>
        ) : state.listError ? (
          <li className="text-sm text-red-600 px-2 py-6">Could not load quotes.</li>
        ) : state.families.length === 0 ? (
          <li className="space-y-1 px-2 py-6 text-sm text-gray-500">
            <p>No matching builder quotes.</p>
            <p>
              CRM quote numbers such as 10670 may exist without a builder quote.
              Use Search in CRM to find them.
            </p>
          </li>
        ) : (
          state.families.map((family) => (
            <FamilyBlock
              key={family.familyKey}
              family={family}
              selected={state.selected}
              expanding={state.expanding === family.familyKey}
              onToggleFamily={() => state.onToggleFamily(family)}
              onToggleMember={(key) => state.onToggleMember(family, key)}
            />
          ))
        )}
      </ul>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={!canImport}
          onClick={async () => {
            const ids = await state.importMutation.mutateAsync()
            onImport(ids)
          }}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-white',
            canImport ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed',
          )}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
          {busy
            ? 'Copying quotes…'
            : state.selectedMembers.length
              ? `Copy ${state.selectedMembers.length} selected quote${state.selectedMembers.length === 1 ? '' : 's'}`
              : 'Copy selected quotes'}
        </button>
      </div>
    </div>
  )
}

function FamilyBlock({
  family,
  selected,
  expanding,
  onToggleFamily,
  onToggleMember,
}: {
  family: QuoteFamily
  selected: Set<string>
  expanding: boolean
  onToggleFamily: () => void
  onToggleMember: (key: string) => void
}) {
  const keys = family.members.map((m) => m.key)
  const checkedCount = keys.filter((k) => selected.has(k)).length
  const allOn = keys.length > 0 && checkedCount === keys.length
  const someOn = checkedCount > 0 && !allOn

  return (
    <li className="rounded-lg border border-gray-200 dark:border-gray-700">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleFamily}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggleFamily()
          }
        }}
        className={cn(
          'flex items-center gap-2.5 px-2.5 py-2 cursor-pointer',
          allOn || someOn ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800',
        )}
      >
        <input
          type="checkbox"
          checked={allOn}
          ref={(el) => { if (el) el.indeterminate = someOn }}
          onChange={onToggleFamily}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select all revisions of ${family.label}`}
          className="h-4 w-4 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{family.label}</p>
          <p className="text-[11px] text-gray-500">
            {family.members.length} revision{family.members.length === 1 ? '' : 's'}
            {expanding ? ' · loading family…' : ''}
          </p>
        </div>
      </div>
      <ul className="border-t border-gray-100 dark:border-gray-800">
        {family.members.map((member) => {
          const checked = selected.has(member.key)
          return (
            <li key={member.key}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onToggleMember(member.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggleMember(member.key)
                  }
                }}
                className={cn(
                  'flex items-center gap-2.5 px-2.5 py-2 cursor-pointer',
                  checked ? 'bg-blue-50/70 dark:bg-blue-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleMember(member.key)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select quote ${member.quoteNumber} ${member.revisionLabel}`}
                  className="h-4 w-4 shrink-0 ml-4"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[13px] font-medium">{member.quoteNumber}</span>
                    <span className="shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px]">
                      {member.revisionLabel}
                    </span>
                    {member.isContract && (
                      <span className="shrink-0 rounded-full bg-blue-100 text-blue-700 px-1.5 py-0.5 text-[10px] font-semibold">
                        Contract
                      </span>
                    )}
                    {member.isVariation && (
                      <span className="shrink-0 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold">
                        Variation
                      </span>
                    )}
                    {!member.builderQuoteId && (
                      <span className="shrink-0 text-[10px] text-gray-500">no scope yet</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-gray-500">
                    <span>{member.name}</span>
                    {member.status && <span>{member.status}</span>}
                    <span>
                      {member.totalExcl != null ? `${formatCurrency(member.totalExcl)} ex GST` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </li>
  )
}
